import { useSeo } from "@/lib/seo";
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';
import Reveal from '@/pages/lot-size/components/Reveal';
import { marketEvents, SESSIONS } from '@/mocks/marketCalendar';
import { eventTimestamp, sessionIsOpen } from './calculations';
import { useMarketClock } from './useMarketClock';
import TodayGlance from './components/TodayGlance';
import TimeZoneSelector from './components/TimeZoneSelector';
import Timeline24h from './components/Timeline24h';
import MarketClock from './components/MarketClock';
import Filters from './components/Filters';
import EventStream from './components/EventStream';
import UpcomingEvents from './components/UpcomingEvents';
import CurrencyFocus from './components/CurrencyFocus';
import EventHeatmap from './components/EventHeatmap';
import MyMarketWatch from './components/MyMarketWatch';
import DateNavigator from './components/DateNavigator';
import DayPlanner from './components/DayPlanner';
import DaySummary from './components/DaySummary';
import EventDrawer from './components/EventDrawer';
import SeoSection from './components/SeoSection';
import BrokerSection from './components/BrokerSection';

export default function MarketCalendarPage() {
  useSeo({
    /* @replace:marketcalendar-title */
    title: 'Lịch Kinh tế & Thị trường Forex | Cập nhật Sự kiện Tài chính - ForexMatrix',
    /* @replace:marketcalendar-description */
    description: 'Theo dõi Lịch Thị trường (Market Calendar) và lịch kinh tế ForexMatrix để cập nhật các tin tức vĩ mô, dữ liệu Non-Farm, lãi suất và sự kiện tài chính ảnh hưởng trực tiếp đến thị trường ngoại hối.',
    /* @replace:marketcalendar-keywords */
    keywords: 'lịch kinh tế forex, lịch thị trường ngoại hối, tin tức tài chính forex, economic calendar forex, sự kiện vĩ mô forex, forex matrix',
    path: '/lich-thi-truong',
  });
  const ctx = useMarketClock();

  const todayEvents = useMemo(() => marketEvents.filter((e) => e.dayOffset === 0), []);

  const currentSession = useMemo(
    () => SESSIONS.find((s) => sessionIsOpen(s, ctx.nowUtcMinutes)) ?? SESSIONS[1],
    [ctx.nowUtcMinutes],
  );

  const upcoming = useMemo(() => {
    return marketEvents
      .filter((e) => e.actual === null)
      .filter((e) => ctx.currencyFilter === 'all' || e.currency === ctx.currencyFilter)
      .map((e) => ({ e, ts: eventTimestamp(e) }))
      .filter((x) => x.ts > ctx.now)
      .sort((a, b) => a.ts - b.ts)
      .slice(0, 3)
      .map((x) => x.e);
  }, [ctx.now, ctx.currencyFilter]);

  const heatmapEvents = useMemo(() => {
    return ctx.dayEvents.filter((e) => {
      if (ctx.impactFilter !== 'all' && e.impact !== ctx.impactFilter) return false;
      if (ctx.categoryFilter !== 'all' && e.category !== ctx.categoryFilter) return false;
      return true;
    });
  }, [ctx.dayEvents, ctx.impactFilter, ctx.categoryFilter]);

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400">
            <Link to="/" className="transition-colors hover:text-slate-600">
              Trang chủ
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="font-semibold text-slate-700">Lịch Thị trường</span>
          </nav>

          {/* Header */}
          <Reveal>
            <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                  <i className="ri-dashboard-line" />
                  Market Command Center
                </span>
                <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                  Lịch Thị trường
                </h1>
                <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
                  Theo dõi các sự kiện kinh tế, phiên giao dịch và những thời điểm quan trọng trong ngày trên một giao diện trực quan.
                </p>
              </div>
              <div className="lg:pb-1">
                <TimeZoneSelector value={ctx.timezoneId} onChange={ctx.setTimezoneId} />
              </div>
            </div>
          </Reveal>

          {/* Today at a Glance */}
          <Reveal delay={40}>
            <div className="mt-8">
              <TodayGlance todayEvents={todayEvents} currentSession={currentSession} />
            </div>
          </Reveal>

          {/* 24H Timeline */}
          <Reveal delay={40}>
            <section className="mt-10">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                    24H Market Timeline
                  </h2>
                  <p className="mt-1.5 text-sm text-slate-500">
                    Các sự kiện trong ngày được đặt trên dòng thời gian 24 giờ. Bấm vào một nút để xem chi tiết.
                  </p>
                </div>
                <span className="hidden rounded-full bg-slate-100 px-3 py-1 font-mono text-[11px] text-slate-500 sm:inline-flex">
                  {ctx.timezone.label}
                </span>
              </div>
              <div className="mt-4 rounded-xl border border-slate-200/80 bg-white p-4">
                <Timeline24h
                  events={ctx.filteredEvents}
                  nowDisplayMinutes={ctx.nowDisplayMinutes}
                  toDisplay={ctx.toDisplay}
                  statusOf={ctx.statusOf}
                  onSelect={(e) => ctx.setSelectedEventId(e.id)}
                />
              </div>
            </section>
          </Reveal>

          {/* Market Clock */}
          <Reveal delay={40}>
            <div className="mt-10">
              <MarketClock nowUtcMinutes={ctx.nowUtcMinutes} toDisplay={ctx.toDisplay} />
            </div>
          </Reveal>

          {/* Filters */}
          <Reveal delay={40}>
            <div className="mt-10">
              <Filters
                currency={ctx.currencyFilter}
                setCurrency={ctx.setCurrencyFilter}
                impact={ctx.impactFilter}
                setImpact={ctx.setImpactFilter}
                category={ctx.categoryFilter}
                setCategory={ctx.setCategoryFilter}
              />
            </div>
          </Reveal>

          {/* Event Stream */}
          <Reveal delay={40}>
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                Dòng sự kiện hôm nay
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">Cuộn qua các sự kiện trong ngày theo trình tự thời gian.</p>
              <div className="mt-4">
                <EventStream
                  events={ctx.filteredEvents}
                  toDisplay={ctx.toDisplay}
                  statusOf={ctx.statusOf}
                  onSelect={(e) => ctx.setSelectedEventId(e.id)}
                />
              </div>
            </section>
          </Reveal>

          {/* Upcoming */}
          <Reveal delay={40}>
            <section className="mt-10">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                  Tiếp theo
                </h2>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
                </span>
              </div>
              <p className="mt-1.5 text-sm text-slate-500">Ba sự kiện quan trọng sắp diễn ra.</p>
              <div className="mt-4">
                <UpcomingEvents
                  events={upcoming}
                  now={ctx.now}
                  toDisplay={ctx.toDisplay}
                  onSelect={(e) => ctx.setSelectedEventId(e.id)}
                />
              </div>
            </section>
          </Reveal>

          {/* Currency Focus */}
          <Reveal delay={40}>
            <div className="mt-10">
              <CurrencyFocus
                events={ctx.dayEvents}
                currency={ctx.currencyFilter}
                setCurrency={ctx.setCurrencyFilter}
                toDisplay={ctx.toDisplay}
                onSelect={(e) => ctx.setSelectedEventId(e.id)}
              />
            </div>
          </Reveal>

          {/* Heatmap */}
          <Reveal delay={40}>
            <div className="mt-10">
              <EventHeatmap events={heatmapEvents} />
            </div>
          </Reveal>

          {/* My Market Watch */}
          <Reveal delay={40}>
            <div className="mt-10">
              <MyMarketWatch watchList={ctx.watchList} toggleWatch={ctx.toggleWatch} />
            </div>
          </Reveal>

          {/* Date navigation + 7-day */}
          <Reveal delay={40}>
            <div className="mt-10">
              <DateNavigator dayOffset={ctx.dayOffset} setDayOffset={ctx.setDayOffset} />
            </div>
          </Reveal>

          {/* Day Planner + Summary */}
          <Reveal delay={40}>
            <div className="mt-10">
              <DayPlanner
                events={ctx.dayEvents}
                watchList={ctx.watchList}
                toDisplay={ctx.toDisplay}
                onSelect={(e) => ctx.setSelectedEventId(e.id)}
              />
            </div>
          </Reveal>
          <Reveal delay={40}>
            <div className="mt-10">
              <DaySummary events={ctx.dayEvents} />
            </div>
          </Reveal>

          {/* SEO + related + broker */}
          <div className="mt-10 space-y-10">
            <Reveal>
              <SeoSection />
            </Reveal>
            <Reveal>
              <BrokerSection />
            </Reveal>
          </div>
        </div>
      </main>

      <EventDrawer event={ctx.selectedEvent} toDisplay={ctx.toDisplay} onClose={() => ctx.setSelectedEventId(null)} />

      <Footer />
      <AppEnding />
    </div>
  );
}